import { Navigate, createBrowserRouter } from 'react-router-dom';

import { FeatureRoute } from '@/components/FeatureRoute';
import { AdminShell } from '@/components/layout/AdminShell';
import { SessionBoundary } from '@/features/auth/components/SessionBoundary';
import { AssignedClientsPage } from '@/pages/AssignedClientsPage';
import { CalendarPage } from '@/pages/CalendarPage';
import { ClientDetailsPage } from '@/pages/ClientDetailsPage';
import { ClientsPage } from '@/pages/ClientsPage';
import { CommunicationsPage } from '@/pages/CommunicationsPage';
import { ContactFormPage } from '@/pages/ContactFormPage';
import { DefinitionFormPage } from '@/pages/DefinitionFormPage';
import { FoundationPage } from '@/pages/FoundationPage';
import { GroupFormPage } from '@/pages/GroupFormPage';
import { MapPage } from '@/pages/MapPage';
import { NewClientPage } from '@/pages/NewClientPage';
import { NoteFormPage } from '@/pages/NoteFormPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { PeoplePage } from '@/pages/PeoplePage';
import { PersonFormPage } from '@/pages/PersonFormPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { ScheduleFormPage } from '@/pages/ScheduleFormPage';
import { ServiceLocationFormPage } from '@/pages/ServiceLocationFormPage';
import { ServiceLocationPage } from '@/pages/ServiceLocationPage';
import { ServiceReportPage } from '@/pages/ServiceReportPage';
import { SettingsDefinitionsPage } from '@/pages/SettingsDefinitionsPage';
import { ShoppingItemFormPage } from '@/pages/ShoppingItemFormPage';
import { ShoppingListPage } from '@/pages/ShoppingListPage';
import { SmsCampaignPage } from '@/pages/SmsCampaignPage';
import { TechnicianDashboardSettingsPage } from '@/pages/TechnicianDashboardSettingsPage';
import { TechnicianRoutePage } from '@/pages/TechnicianRoutePage';

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <SessionBoundary>
        <AdminShell />
      </SessionBoundary>
    ),
    children: [
      { index: true, element: <Navigate to="/foundation" replace /> },
      { path: 'foundation', element: <FoundationPage /> },
      { path: 'foundation/technicians/:technicianId', element: <TechnicianRoutePage /> },
      {
        path: 'calendar',
        element: (
          <FeatureRoute feature="calendar">
            <CalendarPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'map',
        element: (
          <FeatureRoute feature="map">
            <MapPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'clients',
        element: (
          <FeatureRoute feature="clients">
            <ClientsPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'clients/new',
        element: (
          <FeatureRoute feature="clients">
            <NewClientPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'clients/:clientId',
        element: (
          <FeatureRoute feature="clients">
            <ClientDetailsPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'clients/:clientId/contacts/:contactId',
        element: (
          <FeatureRoute feature="clients">
            <ContactFormPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'clients/:clientId/notes/:noteId',
        element: (
          <FeatureRoute feature="clients">
            <NoteFormPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'clients/:clientId/service-locations/:locationId',
        element: (
          <FeatureRoute feature="clients">
            <ServiceLocationPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'clients/:clientId/service-locations/new',
        element: (
          <FeatureRoute feature="clients">
            <ServiceLocationFormPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'clients/:clientId/service-locations/:locationId/reports/:reportId',
        element: (
          <FeatureRoute feature="reports">
            <ServiceReportPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'clients/:clientId/service-locations/:locationId/route-assignments/:assignmentId',
        element: (
          <FeatureRoute feature="clients">
            <ScheduleFormPage kind="recurring" />
          </FeatureRoute>
        ),
      },
      {
        path: 'clients/:clientId/service-locations/:locationId/one-off-visits/:visitId',
        element: (
          <FeatureRoute feature="clients">
            <ScheduleFormPage kind="one-off" />
          </FeatureRoute>
        ),
      },
      {
        path: 'reports',
        element: (
          <FeatureRoute feature="reports">
            <ReportsPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'settings',
        element: (
          <FeatureRoute feature="settings">
            <SettingsDefinitionsPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'settings/:kind/:definitionId',
        element: (
          <FeatureRoute feature="settings">
            <DefinitionFormPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'settings/technician-dashboard',
        element: (
          <FeatureRoute feature="settings">
            <TechnicianDashboardSettingsPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'shopping-list',
        element: (
          <FeatureRoute feature="shopping">
            <ShoppingListPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'shopping-list/:itemId',
        element: (
          <FeatureRoute feature="shopping">
            <ShoppingItemFormPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'communications',
        element: (
          <FeatureRoute feature="communications">
            <CommunicationsPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'communications/groups/:groupId',
        element: (
          <FeatureRoute feature="communications">
            <GroupFormPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'communications/sms/:campaignId',
        element: (
          <FeatureRoute feature="communications">
            <SmsCampaignPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'technicians',
        element: (
          <FeatureRoute feature="people">
            <PeoplePage kind="technicians" />
          </FeatureRoute>
        ),
      },
      {
        path: 'technicians/:personId',
        element: (
          <FeatureRoute feature="people">
            <PersonFormPage kind="technicians" />
          </FeatureRoute>
        ),
      },
      {
        path: 'technicians/:personId/clients',
        element: (
          <FeatureRoute feature="people">
            <AssignedClientsPage />
          </FeatureRoute>
        ),
      },
      {
        path: 'managers',
        element: (
          <FeatureRoute feature="people">
            <PeoplePage kind="managers" />
          </FeatureRoute>
        ),
      },
      {
        path: 'managers/:personId',
        element: (
          <FeatureRoute feature="people">
            <PersonFormPage kind="managers" />
          </FeatureRoute>
        ),
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
]);
